import { FormProvider } from 'react-hook-form';
import type { WizardFormStep } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { FormPersonData } from '@gov-nx/module/page';
import { usePodporaContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	DataTable,
	FormInput,
	FormWidthElementLayout,
	LayoutGap,
} from '@gov-nx/ui/web';
import { nameFormatting } from '@gov-nx/utils/common';

interface PodporaPersonFormProps {
	formMethods: WizardFormStep<FormPersonData>['formDefinition'];
	shouldRenderPlaceholders?: boolean;
}

export const SupportPersonForm = ({
	formMethods,
	shouldRenderPlaceholders = true,
}: PodporaPersonFormProps) => {
	const { individualPerson, controls, isLogged, code } = usePodporaContext();
	const { t } = useLocale(code);

	return (
		<FormProvider {...formMethods.formMethods}>
			<LayoutGap gap="xl">
				<LayoutGap gap="m">
					{isLogged ? (
						<>
							<p className="text-body-m!">
								{t('formular.zprava.predvyplneny-udaje')}
							</p>
							<DataTable responsive>
								<tbody>
									<tr>
										<td className="sm:w-1/2! font-normal">
											{t('formular.pole.jmeno')}
										</td>
										<td className="font-bold">
											{nameFormatting(individualPerson?.jmeno)}
										</td>
									</tr>
									<tr>
										<td className="font-normal">
											{t('formular.pole.prijmeni')}
										</td>
										<td className="font-bold">
											{nameFormatting(individualPerson?.prijmeni)}
										</td>
									</tr>
								</tbody>
							</DataTable>
						</>
					) : (
						<FormWidthElementLayout>
							<LayoutGap gap="m">
								<FormInput
									field={{
										size: 'm',
										name: 'jmeno',
										isDisabled: controls.processLoading,
										placeholder: shouldRenderPlaceholders
											? t('formular.placeholders.jmeno')
											: undefined,
									}}
									label={{ children: t('formular.pole.jmeno') }}
								/>
								<FormInput
									field={{
										name: 'prijmeni',
										isDisabled: controls.processLoading,
										placeholder: shouldRenderPlaceholders
											? t('formular.placeholders.prijmeni')
											: undefined,
									}}
									label={{ children: t('formular.pole.prijmeni') }}
								/>
							</LayoutGap>
						</FormWidthElementLayout>
					)}
				</LayoutGap>
				<FormWidthElementLayout>
					<LayoutGap gap="m">
						<FormInput
							field={{
								type: 'email',
								name: 'email',
								isDisabled: controls.processLoading,
								placeholder: shouldRenderPlaceholders
									? t('formular.placeholders.email')
									: undefined,
							}}
							label={{ children: t('formular.pole.email') }}
						/>
						<FormInput
							field={{
								type: 'tel',
								name: 'telefon',
								isDisabled: controls.processLoading,
								placeholder: shouldRenderPlaceholders
									? t('formular.placeholders.telefon')
									: undefined,
							}}
							label={{ children: t('formular.pole.telefon') }}
						/>
					</LayoutGap>
				</FormWidthElementLayout>
				<ButtonGroupLayout>
					<Button
						isLoading={controls.processLoading}
						aria-label={t('formular.wcag.pokracovat')}
						isDisabled={controls.processLoading}
						nativeType="submit"
						color="primary"
						size="l"
						type="solid">
						{t('formular.tlacitko.pokracovat')}
					</Button>
				</ButtonGroupLayout>
			</LayoutGap>
		</FormProvider>
	);
};
